export enum AchievementCategory {
  SURVIVAL = 'survival',
  COMBAT = 'combat',
  PROGRESSION = 'progression',
  COLLECTION = 'collection',
  SPECIAL = 'special',
}

export enum AchievementRarity {
  COMMON = 'common',
  RARE = 'rare',
  EPIC = 'epic',
  LEGENDARY = 'legendary',
}

export interface AchievementReward {
  gold?: number;
  ruby?: number;
  equipment?: string;
  title?: string;
}

export interface AchievementConfig {
  id: string;
  name: string;
  description: string;
  category: AchievementCategory;
  rarity: AchievementRarity;
  icon: string;
  targetValue: number;
  currentValue?: number;
  isCompleted?: boolean;
  completedAt?: number;
  reward: AchievementReward;
  hidden?: boolean; // Hidden until unlocked
}

export interface PlayerStats {
  // Survival Stats
  totalGamesPlayed: number;
  totalTimePlayed: number; // milliseconds
  highestLevel: number;
  longestSurvivalTime: number;
  
  // Combat Stats
  totalMonstersKilled: number;
  totalBossesKilled: number;
  totalDamageDealt: number;
  totalTowersBuilt: number;
  
  // Progression Stats
  totalGoldEarned: number;
  totalRubyEarned: number;
  totalXPEarned: number;
  totalEquipmentObtained: number;
  
  // Collection Stats
  earthsUnlocked: number;
  towersUnlocked: number;
  petsUnlocked: number;
  equipmentCollected: number;
  
  // Special Stats
  adsWatched: number;
  dailyLoginsStreak: number;
  perfectGames: number; // Games completed without Earth taking damage
  speedRuns: number; // Games completed under certain time
}

export class AchievementManager {
  private static instance: AchievementManager;
  private achievements: Map<string, AchievementConfig> = new Map();
  private playerStats: PlayerStats;
  private completedAchievements: Set<string> = new Set();

  // Achievement definitions
  private static readonly ACHIEVEMENT_CONFIGS: AchievementConfig[] = [
    // Survival Achievements
    {
      id: 'first_steps',
      name: 'First Steps',
      description: 'Complete your first game',
      category: AchievementCategory.SURVIVAL,
      rarity: AchievementRarity.COMMON,
      icon: '🥾',
      targetValue: 1,
      reward: { gold: 100 },
    },
    {
      id: 'survivor_10',
      name: 'Survivor',
      description: 'Reach level 10',
      category: AchievementCategory.SURVIVAL,
      rarity: AchievementRarity.COMMON,
      icon: '🛡️',
      targetValue: 10,
      reward: { gold: 250, ruby: 5 },
    },
    {
      id: 'veteran_25',
      name: 'Veteran',
      description: 'Reach level 25',
      category: AchievementCategory.SURVIVAL,
      rarity: AchievementRarity.RARE,
      icon: '⚔️',
      targetValue: 25,
      reward: { gold: 500, ruby: 15, equipment: 'veteran_badge' },
    },
    {
      id: 'master_50',
      name: 'Master Defender',
      description: 'Reach level 50',
      category: AchievementCategory.SURVIVAL,
      rarity: AchievementRarity.EPIC,
      icon: '👑',
      targetValue: 50,
      reward: { gold: 1000, ruby: 50, title: 'Master Defender' },
    },
    {
      id: 'legendary_100',
      name: 'Legendary Guardian',
      description: 'Reach level 100',
      category: AchievementCategory.SURVIVAL,
      rarity: AchievementRarity.LEGENDARY,
      icon: '⭐',
      targetValue: 100,
      reward: { gold: 2500, ruby: 100, equipment: 'legendary_core', title: 'Legendary Guardian' },
      hidden: true,
    },

    // Combat Achievements
    {
      id: 'exterminator_100',
      name: 'Exterminator',
      description: 'Kill 100 monsters',
      category: AchievementCategory.COMBAT,
      rarity: AchievementRarity.COMMON,
      icon: '💀',
      targetValue: 100,
      reward: { gold: 200 },
    },
    {
      id: 'slayer_1000',
      name: 'Monster Slayer',
      description: 'Kill 1,000 monsters',
      category: AchievementCategory.COMBAT,
      rarity: AchievementRarity.RARE,
      icon: '🗡️',
      targetValue: 1000,
      reward: { gold: 750, ruby: 20 },
    },
    {
      id: 'boss_hunter_10',
      name: 'Boss Hunter',
      description: 'Defeat 10 bosses',
      category: AchievementCategory.COMBAT,
      rarity: AchievementRarity.RARE,
      icon: '🎯',
      targetValue: 10,
      reward: { gold: 600, ruby: 25 },
    },
    {
      id: 'architect_50',
      name: 'Tower Architect',
      description: 'Build 50 towers',
      category: AchievementCategory.COMBAT,
      rarity: AchievementRarity.COMMON,
      icon: '🏗️',
      targetValue: 50,
      reward: { gold: 300 },
    },

    // Progression Achievements
    {
      id: 'wealthy_10k',
      name: 'Wealthy',
      description: 'Earn 10,000 total gold',
      category: AchievementCategory.PROGRESSION,
      rarity: AchievementRarity.RARE,
      icon: '💰',
      targetValue: 10000,
      reward: { ruby: 30 },
    },
    {
      id: 'gem_collector_100',
      name: 'Gem Collector',
      description: 'Earn 100 total ruby',
      category: AchievementCategory.PROGRESSION,
      rarity: AchievementRarity.RARE,
      icon: '💎',
      targetValue: 100,
      reward: { gold: 800, equipment: 'ruby_amplifier' },
    },

    // Collection Achievements
    {
      id: 'earth_collector',
      name: 'Planet Collector',
      description: 'Unlock all Earth types',
      category: AchievementCategory.COLLECTION,
      rarity: AchievementRarity.EPIC,
      icon: '🌍',
      targetValue: 6, // Total number of Earth types
      reward: { gold: 1500, ruby: 75, title: 'Planet Master' },
    },
    {
      id: 'tower_master',
      name: 'Tower Master',
      description: 'Unlock all tower types',
      category: AchievementCategory.COLLECTION,
      rarity: AchievementRarity.EPIC,
      icon: '🗼',
      targetValue: 11, // Total number of tower types
      reward: { gold: 1200, ruby: 60, equipment: 'master_blueprint' },
    },

    // Special Achievements
    {
      id: 'perfect_game',
      name: 'Perfect Defense',
      description: 'Complete a game without Earth taking damage',
      category: AchievementCategory.SPECIAL,
      rarity: AchievementRarity.EPIC,
      icon: '✨',
      targetValue: 1,
      reward: { gold: 2000, ruby: 100, title: 'Perfect Guardian' },
    },
    {
      id: 'speed_runner',
      name: 'Speed Runner',
      description: 'Reach level 20 in under 10 minutes',
      category: AchievementCategory.SPECIAL,
      rarity: AchievementRarity.RARE,
      icon: '⚡',
      targetValue: 1,
      reward: { gold: 1000, ruby: 40, equipment: 'speed_booster' },
    },
    {
      id: 'daily_devotee',
      name: 'Daily Devotee',
      description: 'Login for 7 consecutive days',
      category: AchievementCategory.SPECIAL,
      rarity: AchievementRarity.RARE,
      icon: '📅',
      targetValue: 7,
      reward: { gold: 500, ruby: 35, title: 'Devoted Player' },
    },
  ];

  private constructor() {
    this.initializeAchievements();
    this.loadPlayerStats();
    this.loadCompletedAchievements();
  }

  public static getInstance(): AchievementManager {
    if (!AchievementManager.instance) {
      AchievementManager.instance = new AchievementManager();
    }
    return AchievementManager.instance;
  }

  private initializeAchievements(): void {
    AchievementManager.ACHIEVEMENT_CONFIGS.forEach(config => {
      this.achievements.set(config.id, { ...config });
    });
  }

  private loadPlayerStats(): void {
    const saved = localStorage.getItem('playerStats');
    if (saved) {
      this.playerStats = JSON.parse(saved);
    } else {
      this.playerStats = {
        totalGamesPlayed: 0,
        totalTimePlayed: 0,
        highestLevel: 0,
        longestSurvivalTime: 0,
        totalMonstersKilled: 0,
        totalBossesKilled: 0,
        totalDamageDealt: 0,
        totalTowersBuilt: 0,
        totalGoldEarned: 0,
        totalRubyEarned: 0,
        totalXPEarned: 0,
        totalEquipmentObtained: 0,
        earthsUnlocked: 1, // Start with Classic Earth
        towersUnlocked: 4, // Start with 4 basic towers
        petsUnlocked: 1, // Start with 1 pet
        equipmentCollected: 0,
        adsWatched: 0,
        dailyLoginsStreak: 0,
        perfectGames: 0,
        speedRuns: 0,
      };
    }
  }

  private loadCompletedAchievements(): void {
    const saved = localStorage.getItem('completedAchievements');
    if (saved) {
      const completed = JSON.parse(saved);
      this.completedAchievements = new Set(completed);
      
      // Update achievement states
      this.completedAchievements.forEach(id => {
        const achievement = this.achievements.get(id);
        if (achievement) {
          achievement.isCompleted = true;
        }
      });
    }
  }

  private savePlayerStats(): void {
    localStorage.setItem('playerStats', JSON.stringify(this.playerStats));
  }

  private saveCompletedAchievements(): void {
    localStorage.setItem('completedAchievements', JSON.stringify([...this.completedAchievements]));
  }

  // Update player stats and check achievements
  public updateStat(statName: keyof PlayerStats, value: number, isIncrement: boolean = true): void {
    if (isIncrement) {
      this.playerStats[statName] = (this.playerStats[statName] as number) + value;
    } else {
      this.playerStats[statName] = value as any;
    }
    
    this.savePlayerStats();
    this.checkAchievements();
  }

  // Bulk update multiple stats
  public updateStats(stats: Partial<PlayerStats>): void {
    Object.entries(stats).forEach(([key, value]) => {
      if (typeof value === 'number') {
        this.playerStats[key as keyof PlayerStats] = value as any;
      }
    });
    
    this.savePlayerStats();
    this.checkAchievements();
  }

  // Check all achievements for completion
  private checkAchievements(): AchievementConfig[] {
    const newlyCompleted: AchievementConfig[] = [];

    this.achievements.forEach(achievement => {
      if (achievement.isCompleted) return;

      let currentValue = 0;
      
      // Map achievement IDs to stat values
      switch (achievement.id) {
        case 'first_steps':
          currentValue = this.playerStats.totalGamesPlayed;
          break;
        case 'survivor_10':
        case 'veteran_25':
        case 'master_50':
        case 'legendary_100':
          currentValue = this.playerStats.highestLevel;
          break;
        case 'exterminator_100':
        case 'slayer_1000':
          currentValue = this.playerStats.totalMonstersKilled;
          break;
        case 'boss_hunter_10':
          currentValue = this.playerStats.totalBossesKilled;
          break;
        case 'architect_50':
          currentValue = this.playerStats.totalTowersBuilt;
          break;
        case 'wealthy_10k':
          currentValue = this.playerStats.totalGoldEarned;
          break;
        case 'gem_collector_100':
          currentValue = this.playerStats.totalRubyEarned;
          break;
        case 'earth_collector':
          currentValue = this.playerStats.earthsUnlocked;
          break;
        case 'tower_master':
          currentValue = this.playerStats.towersUnlocked;
          break;
        case 'perfect_game':
          currentValue = this.playerStats.perfectGames;
          break;
        case 'speed_runner':
          currentValue = this.playerStats.speedRuns;
          break;
        case 'daily_devotee':
          currentValue = this.playerStats.dailyLoginsStreak;
          break;
      }

      achievement.currentValue = currentValue;

      // Check if achievement is completed
      if (currentValue >= achievement.targetValue && !achievement.isCompleted) {
        achievement.isCompleted = true;
        achievement.completedAt = Date.now();
        this.completedAchievements.add(achievement.id);
        newlyCompleted.push(achievement);
      }
    });

    if (newlyCompleted.length > 0) {
      this.saveCompletedAchievements();
    }

    return newlyCompleted;
  }

  // Get all achievements
  public getAllAchievements(): AchievementConfig[] {
    return Array.from(this.achievements.values())
      .filter(achievement => !achievement.hidden || achievement.isCompleted)
      .sort((a, b) => {
        // Sort by completion status, then by rarity, then by name
        if (a.isCompleted !== b.isCompleted) {
          return a.isCompleted ? 1 : -1;
        }
        
        const rarityOrder = { common: 1, rare: 2, epic: 3, legendary: 4 };
        if (rarityOrder[a.rarity] !== rarityOrder[b.rarity]) {
          return rarityOrder[a.rarity] - rarityOrder[b.rarity];
        }
        
        return a.name.localeCompare(b.name);
      });
  }

  // Get achievements by category
  public getAchievementsByCategory(category: AchievementCategory): AchievementConfig[] {
    return this.getAllAchievements().filter(achievement => achievement.category === category);
  }

  // Get completion statistics
  public getCompletionStats(): { completed: number; total: number; percentage: number } {
    const visibleAchievements = Array.from(this.achievements.values())
      .filter(achievement => !achievement.hidden || achievement.isCompleted);
    
    const completed = visibleAchievements.filter(achievement => achievement.isCompleted).length;
    const total = visibleAchievements.length;
    const percentage = total > 0 ? Math.round((completed / total) * 100) : 0;

    return { completed, total, percentage };
  }

  // Get player stats
  public getPlayerStats(): PlayerStats {
    return { ...this.playerStats };
  }

  // Apply achievement reward to user profile
  public static applyAchievementReward(reward: AchievementReward, userProfile: any): void {
    if (reward.gold) {
      userProfile.gold += reward.gold;
    }
    
    if (reward.ruby) {
      userProfile.ruby += reward.ruby;
    }
    
    if (reward.equipment) {
      if (!userProfile.ownedItems) userProfile.ownedItems = [];
      userProfile.ownedItems.push({
        id: reward.equipment,
        level: 1,
        obtainedAt: Date.now(),
        source: 'achievement',
      });
    }
    
    if (reward.title) {
      if (!userProfile.titles) userProfile.titles = [];
      userProfile.titles.push(reward.title);
    }
  }

  // Record game session completion
  public recordGameSession(data: {
    level: number;
    timePlayed: number;
    monstersKilled: number;
    bossesKilled: number;
    towersBuilt: number;
    goldEarned: number;
    rubyEarned: number;
    xpEarned: number;
    earthDamageTaken: number;
    wasSpeedRun?: boolean;
  }): AchievementConfig[] {
    // Update stats
    this.updateStat('totalGamesPlayed', 1);
    this.updateStat('totalTimePlayed', data.timePlayed);
    this.updateStat('totalMonstersKilled', data.monstersKilled);
    this.updateStat('totalBossesKilled', data.bossesKilled);
    this.updateStat('totalTowersBuilt', data.towersBuilt);
    this.updateStat('totalGoldEarned', data.goldEarned);
    this.updateStat('totalRubyEarned', data.rubyEarned);
    this.updateStat('totalXPEarned', data.xpEarned);
    
    // Update highest level if needed
    if (data.level > this.playerStats.highestLevel) {
      this.updateStat('highestLevel', data.level, false);
    }
    
    // Update longest survival time if needed
    if (data.timePlayed > this.playerStats.longestSurvivalTime) {
      this.updateStat('longestSurvivalTime', data.timePlayed, false);
    }
    
    // Check for perfect game
    if (data.earthDamageTaken === 0 && data.level >= 10) {
      this.updateStat('perfectGames', 1);
    }
    
    // Check for speed run
    if (data.wasSpeedRun) {
      this.updateStat('speedRuns', 1);
    }

    return this.checkAchievements();
  }

  // Get progress for a specific achievement
  public getAchievementProgress(achievementId: string): { current: number; target: number; percentage: number } {
    const achievement = this.achievements.get(achievementId);
    if (!achievement) {
      return { current: 0, target: 1, percentage: 0 };
    }
    
    const current = achievement.currentValue || 0;
    const target = achievement.targetValue;
    const percentage = Math.min(Math.round((current / target) * 100), 100);
    
    return { current, target, percentage };
  }

  // Reset all achievements (for testing)
  public resetAchievements(): void {
    this.completedAchievements.clear();
    this.achievements.forEach(achievement => {
      achievement.isCompleted = false;
      achievement.currentValue = 0;
      achievement.completedAt = undefined;
    });
    
    localStorage.removeItem('completedAchievements');
    this.saveCompletedAchievements();
  }

  // Reset player stats (for testing)
  public resetPlayerStats(): void {
    this.loadPlayerStats(); // This will create fresh stats
    localStorage.removeItem('playerStats');
  }
} 